import React from 'react';
import { Page, Text, View, Document, StyleSheet, Font, Image, Svg, Path, Rect } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { IGem } from '../../redux/gem/gemSlice';
import PolorisLogo from '../../assets/img/gem_passport/poloris_icon.png';

Font?.register({
  family: 'inter',
  fonts: [
    // eslint-disable-next-line global-require,import/extensions
    { src: require('../../assets/fonts/Inter_Regular.ttf'), fontWeight: 400 },
    // eslint-disable-next-line global-require,import/extensions
    { src: require('../../assets/fonts/Inter_Medium.ttf'), fontWeight: 800 },
  ],
});
// Create styles
const styles = StyleSheet?.create({
  page: {
    padding: 20,
  },
  // Add new style for inner content with border
  innerContent: {
    border: '1px solid #525E6F',
    borderRadius: 2,
    padding: 10,
    minHeight: '95vh',
  },
  title: {
    fontFamily: 'inter',
    fontSize: 24,
    fontWeight: 800,
    color: '#14181F',
    textAlign: 'center',
    marginBottom: 20,
    marginTop: 10,
  },
  section: { marginBottom: 10 },
  table: { width: '515px', marginLeft: 'auto', marginRight: 'auto' },
  tableRow: { flexDirection: 'row', justifyContent: 'space-between' },
  tableCell: {
    flex: 1,
    padding: 5,
    color: '#525E6F',
    fontSize: '11',
    fontFamily: 'inter',
    fontWeight: 400,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  boldText: { fontFamily: 'inter', fontWeight: 400, color: '#14181F', fontSize: 15 },
  notes: { fontFamily: 'inter', marginTop: 50, fontSize: 8, color: '#14181F' },
  superText: { fontFamily: 'inter', fontSize: 8, verticalAlign: 'super' },
});

interface GemPassportProps {
  gem: IGem;
  // eslint-disable-next-line react/no-unused-prop-types
  isPassportLight: boolean;
  // eslint-disable-next-line react/no-unused-prop-types
  includeAiResponse: boolean;
}

interface TableRowProps {
  title1?: string;
  value1?: string | React.ReactNode;
  title2?: string;
  value2?: string;
  aiData?: string;
  isShowAiData?: boolean;
}

const AiTag = ({ height, width }: { height?: number; width?: number }) => (
  <View style={{ display: 'flex', alignItems: 'flex-start', marginLeft: 4 }}>
    <Svg width={width ?? '28'} height={height ?? 19} viewBox="0 0 28 15" fill="none">
      <Rect y="0.5" width="28" height="14" rx="7" ry="7" fill="#6F7C8E" />
      <Path
        d={
          'M11.1307 10.5H10.1989L12.2926 4.68182H13.3068L15.4006 10.5H14.4688L12.8239 5.73864H12.7784L11.1307' +
          ' 10.5ZM11.2869 8.22159H14.3097V8.96023H11.2869V8.22159ZM17.1186 4.68182V10.5H16.2408V4.68182H17.1186Z'
        }
        fill="white"
      />
    </Svg>
  </View>
);
const AiData = ({ isShow, aiData }: { isShow: boolean; aiData?: string }) => (
  <View style={{ display: isShow ? 'flex' : 'none' }}>
    <Text style={styles.boldText}>
      {' | '} {aiData}
    </Text>
  </View>
);

const TableRow = ({ title1, title2, value1, value2, aiData, isShowAiData }: TableRowProps) => (
  <View style={styles.tableRow}>
    <View style={{ ...styles.tableCell, flex: 2 }}>
      <Text>
        {title1} {'   '}
      </Text>
      <Text style={styles.boldText}>
        {value1}
        {isShowAiData && aiData && <AiData isShow={isShowAiData} aiData={aiData} />}
      </Text>
      {isShowAiData && aiData && <AiTag />}
    </View>

    {title2 && (
      <Text style={styles.tableCell}>
        {title2} {'   '} <Text style={styles.boldText}>{value2}</Text>
      </Text>
    )}
  </View>
);

export const GemPassport = ({ gem, isPassportLight, includeAiResponse }: GemPassportProps) => {
  const {
    systemId,
    appearanceImage,
    species,
    shape,
    color,
    secondaryColor,
    intensity,
    specialDescriptor,
    cut,
    width,
    length,
    depth,
    weight,
    clarity,
    treatment,
    origin,
    location,
    aiResponse,
    version,
  } = gem;

  const uginIdWithAiTag = (
    <View style={styles.tableRow}>
      <View style={{ ...styles.tableCell, flex: 2 }}>
        <Text>Fingerprint UGIN {'   '}</Text>
        <Text style={styles.boldText}>{aiResponse?.uginId}</Text>
        <AiTag />
      </View>
    </View>
  );

  return (
    <Document>
      <Page style={styles.page}>
        <View style={styles.innerContent}>
          <Text style={styles.title}>Gemstone Report</Text>
          <View style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Image cache src={appearanceImage} style={{ width: 92, height: 86 }} />
          </View>

          <Text style={{ ...styles.title, fontSize: 15 }}>
            {species} {color} {weight}ct
            <Text style={{ ...styles.title, fontSize: 15 }}> </Text>
            <Text style={styles.superText}>Note 1</Text>
          </Text>
          <View style={styles.table}>
            <TableRow
              title1="Report No."
              value1={`${systemId}.${version ?? 0.0}`}
              title2="Report Date"
              value2={format(new Date(), 'dd MMM yy')}
            />
            {!isPassportLight && uginIdWithAiTag}
            <TableRow title1="Species" value1={species} title2="Shape" value2={shape} />
            <TableRow title2="Cut" value2={cut} />
            <TableRow
              title1="Color (Primary)"
              value1={color}
              aiData={aiResponse?.colorPrediction}
              isShowAiData={includeAiResponse}
            />
            <TableRow
              title1="Color (Secondary)"
              value1={secondaryColor}
              title2="Weight (ct)"
              value2={weight.toFixed(2).toString()}
              aiData={aiResponse?.secondaryColorPrediction}
              isShowAiData={includeAiResponse}
            />

            <TableRow
              title1="Color (Intensity)"
              value1={intensity ?? 'N/A'}
              title2="Dimensions (mm)"
              value2={`\n${width.toFixed(2)} x ${length.toFixed(2)} x ${depth.toFixed(2)}`}
            />

            <TableRow
              title1="Special Color Descriptor"
              value1={specialDescriptor}
              aiData={aiResponse?.specialColorPrediction}
              isShowAiData={includeAiResponse}
            />

            {/* <TableRow title1="" /> */}

            <View style={{ marginTop: 15 }}>
              <TableRow title1="Treatment" value1={treatment} title2="Clarity" value2={clarity} />
            </View>

            <View style={{ marginTop: 12 }}>
              <TableRow
                title1="Origin"
                value1={`Possess characteristics corresponding to ${species} from : ${origin}`}
              />
            </View>

            <View style={{ marginTop: 10 }}>
              <TableRow title1="Location" value1={location ?? 'N/A'} />
            </View>
          </View>

          <View style={{ ...styles.table, position: 'absolute', bottom: 70, marginLeft: 12 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
              <Text style={{ ...styles.tableCell, flex: 2 }}>Issued by</Text>
            </View>
            <View style={styles.tableRow}>
              <View style={{ ...styles.tableCell, flex: 2 }}>
                <Image cache src={PolorisLogo} style={{ width: 90 }} />
              </View>
            </View>
          </View>
          <View style={[styles.notes, { position: 'absolute', bottom: 30, marginLeft: 20 }]}>
            <Text style={{ fontWeight: 800 }}>Notes:</Text>
            <Text>1. Image of gemstone table with illumination and exposure setting by User</Text>
            <View style={[{ display: 'flex', flexDirection: 'row' }]}>
              <Text>2.</Text>
              <AiTag width={19} height={11} />
              <Text> </Text>
              <Text>Denote inference from AI-model, trained using gemologist expert labelled dataset</Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};
